import React, { useState, useEffect, FormEvent, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '../api/client';
import type { Enable2FAResponse, Verify2FARequest, Verify2FAResponse } from '../types/api';
import AlertMessage from '../components/AlertMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import './Setup2FAPage.css';

type Step = 'loading' | 'scan' | 'verify';

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

function StoreBtn({ href, className, ariaLabel, children }: {
  href: string; className: string; ariaLabel: string; children: React.ReactNode;
}) {
  if (isMobile) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={className} aria-label={ariaLabel}>{children}</a>;
  }
  return <span className={`${className} store-btn-desktop`} aria-label={ariaLabel}>{children}</span>;
}

export default function Setup2FAPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('loading');
  const [setupData, setSetupData] = useState<Enable2FAResponse | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    (async () => {
      try {
        const data = await apiPost<Enable2FAResponse>('/api/auth/2fa/enable');
        setSetupData(data);
        setStep('scan');
      } catch (err: any) {
        setError(err.message || 'Failed to initialize 2FA setup.');
        setStep('scan');
      }
    })();
  }, []);

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const body: Verify2FARequest = { code };
      await apiPost<Verify2FAResponse>('/api/auth/2fa/verify', body);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (step === 'loading') {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <p className="auth-subtitle">Setting up extra security for your account</p>
          <div className="divider" />
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // step === 'scan' or 'verify'
  return (
    <div className="auth-page">
      <div className="auth-container setup-2fa-container">
        <h1 className="auth-title">Cedar Vault</h1>
        <p className="auth-subtitle">Secure your account</p>
        <div className="divider" />

        <AlertMessage type="error" message={error} />

        <div className="setup-2fa-intro">
          <p>
            To protect your family's important information, we use a free app on your phone
            as a second layer of security. This means even if someone guesses your password,
            they still can't get into your vault.
          </p>
        </div>

        <div className="setup-2fa-steps">
          <div className="setup-2fa-step-card">
            <div className="setup-2fa-step-number">1</div>
            <div className="setup-2fa-step-content">
              <strong>Install a free authenticator app on your phone</strong>
              {isMobile ? (
                <p>Tap the button for your phone type — it opens the app store directly. Both apps are free.</p>
              ) : (
                <p>
                  Pick up your phone and open the <strong>App Store</strong> (iPhone) or{' '}
                  <strong>Google Play</strong> (Android). Search for one of the free apps below and install it.
                </p>
              )}

              <div className="setup-2fa-app-tiles">
                {/* Microsoft Authenticator */}
                <div className="setup-2fa-app-tile">
                  <div className="setup-2fa-app-icon">
                    <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle cx="22" cy="22" r="22" fill="#0078D4"/>
                      <rect x="10" y="10" width="11" height="11" fill="#F25022"/>
                      <rect x="23" y="10" width="11" height="11" fill="#7FBA00"/>
                      <rect x="10" y="23" width="11" height="11" fill="#00A4EF"/>
                      <rect x="23" y="23" width="11" height="11" fill="#FFB900"/>
                    </svg>
                  </div>
                  <div className="setup-2fa-app-info">
                    <span className="setup-2fa-app-name">Microsoft Authenticator</span>
                    <span className="setup-2fa-app-desc">Recommended — very easy to use</span>
                    <div className="setup-2fa-store-buttons">
                      <StoreBtn
                        href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458"
                        className="setup-2fa-store-btn store-apple"
                        ariaLabel="Get Microsoft Authenticator on the App Store (iPhone)"
                      >
                        <span className="store-btn-icon">
                          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11"/>
                          </svg>
                        </span>
                        iPhone
                      </StoreBtn>
                      <StoreBtn
                        href="https://play.google.com/store/apps/details?id=com.azure.authenticator"
                        className="setup-2fa-store-btn store-android"
                        ariaLabel="Get Microsoft Authenticator on Google Play (Android)"
                      >
                        <span className="store-btn-icon">
                          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-7.27-2.83-2.83L3.18 23.76zM.49 1.27C.18 1.6 0 2.1 0 2.76v18.48c0 .66.18 1.16.49 1.49l.08.08 10.35-10.35v-.24L.57 1.19l-.08.08zM20.13 10.6l-2.7-1.56-3.17 3.17 3.17 3.17 2.73-1.58c.78-.45.78-1.75-.03-2.2zM4.17.24L16.77 7.5l-2.83 2.83L3.18.24C3.53-.02 3.87-.07 4.17.24z"/>
                          </svg>
                        </span>
                        Android
                      </StoreBtn>
                    </div>
                  </div>
                </div>

                {/* Google Authenticator */}
                <div className="setup-2fa-app-tile">
                  <div className="setup-2fa-app-icon">
                    <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle cx="22" cy="22" r="22" fill="#ffffff" stroke="#e0e0e0" strokeWidth="1"/>
                      <path d="M22 11c-6.08 0-11 4.92-11 11s4.92 11 11 11c9.17 0 12.43-7.85 11.54-13H22v4h7.04c-.76 3.59-3.93 6-7.04 6-4.42 0-8-3.58-8-8s3.58-8 8-8c2.08 0 3.95.8 5.37 2.1l2.93-2.93A10.94 10.94 0 0 0 22 11z" fill="#4285F4"/>
                      <path d="M11.62 17.12l3.28 2.41A6 6 0 0 1 22 16c2.08 0 3.95.8 5.37 2.1l2.93-2.93A10.94 10.94 0 0 0 22 11c-4.47 0-8.33 2.65-10.38 6.12z" fill="#EA4335"/>
                      <path d="M22 33c3.02 0 5.76-1.12 7.83-2.95l-3.1-2.63A6 6 0 0 1 22 29a6 6 0 0 1-5.88-4.79l-3.29 2.54C14.86 30.56 18.18 33 22 33z" fill="#34A853"/>
                      <path d="M11.62 17.12l3.28 2.41A5.98 5.98 0 0 0 14 22a5.98 5.98 0 0 0 .88 3.15l-3.27 2.52C10.57 25.73 10 23.94 10 22c0-1.79.49-3.47 1.62-4.88z" fill="#FBBC05"/>
                    </svg>
                  </div>
                  <div className="setup-2fa-app-info">
                    <span className="setup-2fa-app-name">Google Authenticator</span>
                    <span className="setup-2fa-app-desc">Simple and reliable</span>
                    <div className="setup-2fa-store-buttons">
                      <StoreBtn
                        href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                        className="setup-2fa-store-btn store-apple"
                        ariaLabel="Get Google Authenticator on the App Store (iPhone)"
                      >
                        <span className="store-btn-icon">
                          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11"/>
                          </svg>
                        </span>
                        iPhone
                      </StoreBtn>
                      <StoreBtn
                        href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                        className="setup-2fa-store-btn store-android"
                        ariaLabel="Get Google Authenticator on Google Play (Android)"
                      >
                        <span className="store-btn-icon">
                          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-7.27-2.83-2.83L3.18 23.76zM.49 1.27C.18 1.6 0 2.1 0 2.76v18.48c0 .66.18 1.16.49 1.49l.08.08 10.35-10.35v-.24L.57 1.19l-.08.08zM20.13 10.6l-2.7-1.56-3.17 3.17 3.17 3.17 2.73-1.58c.78-.45.78-1.75-.03-2.2zM4.17.24L16.77 7.5l-2.83 2.83L3.18.24C3.53-.02 3.87-.07 4.17.24z"/>
                          </svg>
                        </span>
                        Android
                      </StoreBtn>
                    </div>
                  </div>
                </div>
              </div>

              <p className="setup-2fa-hint">
                {isMobile
                  ? 'Already have one of these apps? Great — skip to step 2!'
                  : 'Already have one of these apps on your phone? Great — skip to step 2!'}
              </p>
            </div>
          </div>

          <div className="setup-2fa-step-card">
            <div className="setup-2fa-step-number">2</div>
            <div className="setup-2fa-step-content">
              <strong>Add Cedar Vault to your authenticator app</strong>
              {isMobile ? (
                <p className="setup-2fa-scan-instructions">
                  Tap the button below. On iPhone it may open <strong>Apple Passwords</strong> — that works great!
                  Select your Cedar Vault account and tap to add the code.
                </p>
              ) : (
                <p className="setup-2fa-scan-instructions">
                  Tap <strong>"Add to Authenticator"</strong> if you're on your phone, or scan
                  the QR code below with your phone's authenticator app. You can also enter
                  the key manually.
                </p>
              )}
              {setupData && (
                <>
                  <a
                    href={setupData.authenticatorUri}
                    className="btn-primary setup-2fa-open-app-btn"
                  >
                    Add to Authenticator
                  </a>
                  {!isMobile && (
                    <div className="setup-2fa-qr">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.authenticatorUri)}`}
                        alt="QR code for authenticator app"
                        width="200"
                        height="200"
                      />
                    </div>
                  )}
                  <p className="setup-2fa-manual-key-label">
                    {isMobile
                      ? <>Prefer Microsoft or Google Authenticator? Open that app, tap <strong>+</strong> → "Other account", then enter this key:</>
                      : <>Or enter this key manually in your authenticator app:</>
                    }
                  </p>
                  <div className="setup-2fa-manual-key">{setupData.sharedKey}</div>
                </>
              )}
            </div>
          </div>

          <div className="setup-2fa-step-card">
            <div className="setup-2fa-step-number">3</div>
            <div className="setup-2fa-step-content">
              <strong>Enter your Cedar Vault 6-digit code</strong>
              <p>
                {isMobile
                  ? 'Once added, your authenticator app will show your Cedar Vault 6-digit code that changes every 30 seconds. Type that code below to confirm everything is working.'
                  : 'After scanning, your authenticator app will show your Cedar Vault 6-digit code that changes every 30 seconds. Type that code below to confirm everything is working.'
                }
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleVerify}>
          <div className="form-group">
            <label className="form-label" htmlFor="verifyCode">Your Cedar Vault 6-digit code</label>
            <input
              id="verifyCode"
              className="form-input tfa-code-input"
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="000000"
              required
              autoComplete="one-time-code"
            />
          </div>

          <button className="btn-primary" type="submit" disabled={loading}>
            {loading ? <LoadingSpinner small /> : 'Verify & Enable'}
          </button>
        </form>

        <p className="setup-2fa-help">
          Having trouble? Contact us at{' '}
          <a href="mailto:support@cedarvaultapp.com">support@cedarvaultapp.com</a>
          {' '}and we'll walk you through it.
        </p>
      </div>
    </div>
  );
}
